import React, {useCallback, useState} from 'react';

import cs from 'classnames';
import debounce from 'lodash/debounce';

import {
  useCurrentUserState,
  useDeleteEmail,
  useResendConfirmationEmail,
  useUpdateCurrentUser,
} from 'app/current-user/api';
import {ActionsButton} from 'app/general/widgets/ActionsButton';
import {CheckboxField} from 'app/general/widgets/CheckboxField';
import {LoadableEditableAvatar} from 'app/general/widgets/EditableAvatar/LoadableEditableAvatar';
import {SweetSelectField} from 'app/general/widgets/SweetSelectField';
import {TextboxField} from 'app/general/widgets/TextboxField';
import {useLanguageSettings, useTimezoneSettings} from 'app/settings/api';
import {useUpdateUser} from 'app/user/api';
import {gt} from 'app/utils/i18n';

import type {OptionType} from 'app/settings/types';
import type {AuthenticatedUserSerializedType} from 'sweet-types';

interface UserFormDataType {
  name: string;
  email: string;
}

export function UserProfile() {
  const user = useCurrentUserState().data!;

  const [updateCurrentUser] = useUpdateCurrentUser();
  const [updateUser] = useUpdateUser();
  const [userFormData, setUserFormData] = useState<UserFormDataType>({
    // name/email updated via user api.
    name: user.name,
    email: user.email,
  });

  const [currentUserFormData, setCurrentUserFormData] = useState({
    // language/timezone updated via current-user api
    language: user.language,
    timezone: user.timezone,
    is_diagrams_expanded: user.is_diagrams_expanded,
  });

  const [languageLabel, setLanguageLabel] = useState(user.language_label);

  const debouncedUserSave = useCallback(
    debounce((newFormData: Partial<UserFormDataType>) => {
      updateUser({id: user.id, ...newFormData});
    }, 2000),
    []
  );

  function handleUserFormChange(
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) {
    debouncedUserSave({
      [e.target.name]: e.target.value,
    } as Partial<UserFormDataType>);
    setUserFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  }

  function handleSelectChange(name: string) {
    function innerHandleSelectChange(value: OptionType) {
      const promise = updateCurrentUser({[name]: value.id}).unwrap();
      setCurrentUserFormData((prev) => ({
        ...prev,
        [name]: value.id,
      }));
      if (name === 'language') {
        setLanguageLabel(value.label);
      }
      return promise;
    }
    return innerHandleSelectChange;
  }

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {name, checked} = e.target;
    updateCurrentUser({[name]: checked});
    setCurrentUserFormData((prev) => ({
      ...prev,
      [name]: checked,
    }));
  };

  return (
    <div className="pt-4 form">
      <h4 className="pb-2 border-bottom">{gt('Your Profile')}</h4>

      <TextboxField
        key="name"
        label={gt('Name')}
        name="name"
        id="id_name"
        maxLength={30}
        onChange={handleUserFormChange}
        value={userFormData.name}
      />

      <LoadableEditableAvatar
        key="avatar_image"
        label={gt('Profile Picture')}
        id="id_avatar_image"
        initialImage={user.avatar_image ?? undefined}
        onChange={(avatar_image) => {
          updateCurrentUser({avatar_image});
          setCurrentUserFormData((prev) => ({...prev, avatar_image}));
        }}
        messages={{
          uploadError: 'uploadError',
          success: 'success',
          removed: 'removed',
          removalFailed: 'failed',
          invalidImage: 'invalid',
        }}
      />

      <TextboxField
        key="email"
        label={gt('Email')}
        name="email"
        id="id_email"
        maxLength={254}
        onChange={handleUserFormChange}
        value={userFormData.email}
      />
      {user.emailaddresses.length > 1 ? <EmailAddresses user={user} /> : null}

      <SweetSelectField<OptionType>
        label={gt('Language')}
        placeholder={gt('Select your preferred language.')}
        id="id_language"
        isClearable={false}
        useDataHook={useLanguageSettings}
        onChange={handleSelectChange('language')}
        selected={{id: currentUserFormData.language, label: languageLabel}}
        formatOptionLabel={(option: OptionType) => option.label}
      />

      <SweetSelectField<OptionType>
        label={gt('Timezone')}
        selected={{
          id: currentUserFormData.timezone,
          label: currentUserFormData.timezone,
        }}
        placeholder={gt('Select your timezone.')}
        id="id_timezone"
        isClearable={false}
        useDataHook={useTimezoneSettings}
        onChange={handleSelectChange('timezone')}
        formatOptionLabel={(option: OptionType) => option.label}
      />

      <CheckboxField
        label={gt('Expand procedure diagrams')}
        name="is_diagrams_expanded"
        id="id_is_diagrams_expanded"
        helpText={gt('Show procedure diagrams expanded by default')}
        onChange={handleCheckboxChange}
        value={currentUserFormData.is_diagrams_expanded}
      />
    </div>
  );
}

interface EmailAddressProps {
  user: AuthenticatedUserSerializedType;
}

function EmailAddresses({user}: EmailAddressProps) {
  return (
    <div className="my-4 p-4 col-xl-6 border rounded">
      <p>
        {gt(
          "Changing your email address? Confirm the change by clicking the link in the verification email we've sent you."
        )}
      </p>
      <ul className="list-group">
        {user.emailaddresses.map((email) => (
          <EmailAddress key={email.id} email={email} />
        ))}
      </ul>
    </div>
  );
}

function EmailAddress({
  email,
}: {
  email: AuthenticatedUserSerializedType['emailaddresses'][0];
}) {
  const [deleteEmail] = useDeleteEmail();
  const [resend] = useResendConfirmationEmail();
  return (
    <li className="list-group-item d-flex justify-content-between align-items-center">
      <div>
        <span
          className={cs({
            'text-secondary': !email.primary,
            'text-dark': email.primary,
          })}
        >
          {email.email}
        </span>
        {email.verified && (
          <span className="ml-2 badge badge-success">{gt('verified')}</span>
        )}
        {email.primary && (
          <span className="ml-2 badge badge-primary">{gt('primary')}</span>
        )}
        {!email.verified && (
          <span className="ml-2 badge badge-warning">{gt('not verified')}</span>
        )}
      </div>
      {!email.primary && (
        <ActionsButton
          actions={[
            {
              text: gt('Resend Confirmation Email'),
              icon: 'far fa-paper-plane',
              key: 'resend',
              condition: () => !email.verified,
              callback: () => resend({id: email.id}),
            },
            {
              text: gt('Remove Email'),
              icon: 'far fa-trash',
              key: 'delete',
              className: 'text-danger',
              condition: () => !email.primary,
              callback: () => deleteEmail({url: email.url}),
            },
          ]}
        />
      )}
    </li>
  );
}
